import * as React from 'react';
import { cn } from '../lib/utils.js';
import { Table, TableBody, TableHeader, TableRow } from './table.js';
import {
  useColumnResize,
  useRowDragDrop,
  resolveColResizable,
  snapshotThWidths,
  computeTableWidthInfo,
  buildRowPropsFor,
  resolveScrollContainerRef,
  resolveVirtualizedLayout,
  resolveRowLinkProps,
  resolveStickyHeaderOffset,
  resolveUseFrozenHeader,
  resolveFrozenHeaderLayout,
  useFrozenHeaderSync,
} from './data-table-hooks.js';
import { renderHeaderRowCells, type HeaderRowContext } from './data-table-header-row.js';
import { useMeasuredElement } from './use-measured-element.js';
import {
  TableVirtualizerSentinel,
  ScrollIndicators,
  useRowSelectionState,
  RowLinkCell,
  ROW_LINK_ANCHOR_CLASSNAME,
  INTERACTIVE_CELL_CLASSNAME,
} from './data-table-helpers.js';
import { TableRows } from './data-table-rows.js';
import type { TableVirtualizerBundle } from './data-table-virtualizer.js';

// Shared between the real table and its frozen-header copy so they stay pixel-identical.
const DATA_TABLE_TEXT_CLASSNAME = 'text-[13px]';

// ─── Types ───────────────────────────────────────────────────

interface ColumnDef<T> {
  id: string;
  label: string;
  align: 'left' | 'right' | 'center';
  renderCell: (item: T) => React.ReactNode;
  /**
   * Optional custom header content, overriding the plain `label` text (e.g. a
   * structural header-level checkbox for a selection column). Falls back to
   * rendering `label` when omitted.
   */
  renderHeader?: () => React.ReactNode;
  headerClassName?: string;
  /** Fixed column width in pixels. When set, both th and td get this width and overflow is hidden. */
  width?: number;
  /** If true, this column can be sorted. Requires sortKey. */
  sortable?: boolean;
  /** The sort key passed to onSort callback when this column header is clicked. */
  sortKey?: string;
  /**
   * Whether this column shows a resize handle.
   * When not set, falls back to the table-level `resizableColumns` prop (default true).
   * Explicitly setting `true` or `false` overrides the table default for this column only.
   */
  resizable?: boolean;
  /**
   * Extra className applied to every `<td>` in this column (overrides default px-4 py-3).
   * Accepts a static string or a function of the row item for per-row styling.
   */
  cellClassName?: string | ((item: T) => string);
  /** Per-row inline style for this column's `<td>` (e.g. status-hatch backgrounds). */
  cellStyle?: (item: T) => React.CSSProperties;
  /**
   * When true, this column is excluded from both the resize handle and column-header
   * drag-reorder, regardless of the table-level `resizableColumns` default or the
   * presence of `onColumnReorder`.
   */
  fixed?: boolean;
}

// ─── Helpers ─────────────────────────────────────────────────

function resolveColWidth(
  colWidths: Record<string, number>,
  colId: string,
  colWidth?: number,
): number | undefined {
  return colWidths[colId] ?? colWidth;
}

// ─── Component ───────────────────────────────────────────────

interface SortOrder {
  key: string;
  order: number;
  dir: 'asc' | 'desc';
}

interface DataTableProps<T> {
  columns: ColumnDef<T>[];
  data: T[];
  getRowKey: (item: T) => string;
  getRowTestId?: (item: T) => string;
  /** Optional extra HTML attributes to spread onto each `<tr>` element. */
  getRowAttributes?: (item: T) => Record<string, string | undefined>;
  /**
   * Optional per-row extra className. Merged with built-in stripe and hover classes.
   * Receives the row item and its zero-based index.
   */
  getRowClassName?: (item: T, index: number) => string | undefined;
  className?: string;
  footer?: React.ReactNode;
  /** Currently active sort column key */
  sortBy?: string;
  /** Current sort direction */
  sortDir?: 'asc' | 'desc';
  /** Called when a sortable column header is clicked, with whether Shift was held */
  onSort?: (sortKey: string, shiftKey: boolean) => void;
  /**
   * Called when a table row is clicked (also triggered on Enter key for a11y).
   *
   * Deprecated in favor of `renderRowLink` for row-to-detail navigation: this wires a
   * plain `onClick` on the `<tr>` itself, a known accessibility anti-pattern (breaks
   * keyboard focus semantics, breaks middle-click/ctrl-click "open in new tab", and a
   * same-row drag between two cells can misfire it via the browser's click-synthesis
   * onto the nearest common ancestor). Kept only for callers not yet migrated;
   * ignored entirely whenever `renderRowLink` is also provided.
   */
  onRowClick?: (item: T) => void;
  /**
   * Renders a real, focusable anchor into a dedicated near-zero-width structural first
   * column of every row, implementing the "stretched link" pattern instead of the
   * `<tr onClick>` anti-pattern above (see `ROW_LINK_ANCHOR_CLASSNAME` in
   * data-table-helpers.tsx for the full mechanism and z-index scheme). Router-agnostic
   * by design — return a TanStack Router `Link`, a plain `<a href>`, or any component
   * that forwards `className` to a real anchor, spreading `ROW_LINK_ANCHOR_CLASSNAME`
   * (exported alongside this prop) onto it so its `::after` pseudo-element stretches to
   * cover the whole row. The anchor MUST have a meaningful accessible name (e.g.
   * `aria-label`) — it is rendered small/icon-only, not empty/decorative. Any OTHER
   * per-row interactive cell (checkboxes, popover triggers) must apply
   * `INTERACTIVE_CELL_CLASSNAME` so it keeps winning hit-tests over the overlay.
   * Supersedes `onRowClick` when both are provided.
   */
  renderRowLink?: (item: T) => React.ReactNode;
  /**
   * Multi-sort configuration. When provided, matching columns display a compact
   * badge ("N↑" or "N↓") and clicking those columns does NOT trigger onSort.
   * Single-sort (sortBy/sortDir/onSort) behavior is preserved for columns NOT in sortOrders.
   */
  sortOrders?: SortOrder[];
  /**
   * When true, rows are draggable via HTML5 DnD. A subtle drag cursor is added to each row.
   * onRowReorder is called with the from/to indices after a drop.
   * Drag handles should be rendered inside a column's renderCell; this prop only adds
   * the DnD event handlers to the <tr> elements.
   */
  draggableRows?: boolean;
  onRowReorder?: (fromIndex: number, toIndex: number) => void;
  /**
   * Controlled column-reorder callback. When provided, dropping a dragged column header
   * calls this with the dragged and target column ids instead of mutating any internal
   * state — the parent is expected to recompute the `columns` prop with the new order.
   * When omitted, header drag-and-drop has no effect.
   */
  onColumnReorder?: (fromId: string, toId: string) => void;
  /**
   * When true (the default), every column without an explicit `resizable` flag gets a resize
   * handle. Set to false to opt out globally; individual columns can still override with
   * `resizable: true`.
   */
  resizableColumns?: boolean;
  /**
   * Called once when a column-resize drag ends (mouseup), with the dragged column's id and
   * final pixel width — never called during intermediate mousemove events. Wire this to a
   * `useColumnPreferences` hook's `setColumnWidth` to persist resized widths. When omitted,
   * resize behavior is unchanged (in-memory width only).
   */
  onColumnResizeEnd?: (columnId: string, widthPx: number) => void;
  /**
   * Optional controlled row-selection state.
   * When present: a checkbox column is prepended and the header checkbox shows
   * checked / indeterminate / unchecked state based on selection coverage.
   * When absent: no checkbox column, no behavioural change (backwards compatible).
   */
  rowSelection?: {
    selectedIds: Set<string | number>;
    onChange: (newIds: Set<string | number>) => void;
    /** Total rows matching the current filter (used for "select all N matching" affordance). */
    totalMatching?: number;
  };
  /**
   * Opt-in row virtualization, from `useTableVirtualizer`. When provided, DataTable renders
   * only `virtualItems` as absolutely-positioned rows inside a bounded, vertically-scrollable
   * container with a sticky header, and uses `scrollContainerRef` as its OWN horizontal
   * scroll-shadow-indicator container (so the indicators track the same scrolled element the
   * virtualizer measures, rather than a second, disconnected ref on the same div). When
   * omitted (the default), DataTable renders every row in normal document flow exactly as
   * before this capability existed — no sticky header, no bounded height.
   */
  virtualizer?: TableVirtualizerBundle;
  /**
   * Pins the `<thead>` via `position: sticky` at this pixel offset from the top of
   * whatever scroll container it's in — independent of `virtualizer`. Use this for
   * unbounded, page-scrolled tables (no `virtualizer`) where the caller's own layout
   * has fixed chrome above the table (e.g. a `sticky` toolbar) whose height must be
   * cleared. Unlike `virtualizer`, this does NOT switch on row virtualization or a
   * bounded scroll container — the page keeps owning the scroll.
   */
  stickyHeaderOffsetPx?: number;
  /**
   * Receives the table's horizontal scroll container on mount (and `null` on
   * unmount). Lets a caller drive that element from its own control — e.g.
   * `StickyHorizontalScrollbar`, when the native scrollbar sits out of reach at
   * the bottom of an unbounded, page-scrolled table.
   */
  onScrollContainerElement?: (element: HTMLDivElement | null) => void;
  /** Extra classes for the scroll container itself (not the outer wrapper). */
  scrollContainerClassName?: string;
}

function DataTable<T>({
  columns,
  data,
  getRowKey,
  getRowTestId,
  getRowClassName,
  getRowAttributes,
  className,
  footer,
  sortBy,
  sortDir,
  onSort,
  onRowClick,
  renderRowLink,
  sortOrders,
  draggableRows,
  onRowReorder,
  resizableColumns = true,
  rowSelection,
  onColumnReorder,
  onColumnResizeEnd,
  virtualizer,
  stickyHeaderOffsetPx,
  onScrollContainerElement,
  scrollContainerClassName,
}: DataTableProps<T>) {
  const [draggedColumnId, setDraggedColumnId] = React.useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = React.useState<string | null>(null);

  const { colWidths, isResizing, handleResizeStart } = useColumnResize({
    initialColumns: columns,
    onResizeEnd: onColumnResizeEnd,
  });

  const {
    dragOverRowIndex,
    handleRowDragStart,
    handleRowDragOver,
    handleRowDrop,
    handleRowDragEnd,
  } = useRowDragDrop({ onRowReorder });

  const theadRef = React.useRef<HTMLTableSectionElement>(null);
  // When virtualized, the scroll-shadow-indicator effect below MUST measure the identical
  // DOM node the virtualizer's own getScrollElement() measures — otherwise the indicators
  // silently stop working. Reuse the caller's ref instead of maintaining a second one.
  const localScrollRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = resolveScrollContainerRef(virtualizer?.scrollContainerRef, localScrollRef);
  const virtualizedLayout = resolveVirtualizedLayout(Boolean(virtualizer), virtualizer?.totalSize);
  const stickyHeaderOffset = resolveStickyHeaderOffset(stickyHeaderOffsetPx);
  const useFrozenHeader = resolveUseFrozenHeader(stickyHeaderOffsetPx, virtualizer);
  const frozenHeaderLayout = resolveFrozenHeaderLayout(
    useFrozenHeader,
    virtualizedLayout.headerClassName,
    stickyHeaderOffset,
  );
  const { frozenHeaderTableRef, measuredHeaderWidths } = useFrozenHeaderSync(
    useFrozenHeader,
    theadRef,
    scrollRef,
    columns,
  );
  // The frozen header is a sibling BEFORE the scroll container, so it reserves its own
  // height in normal flow — on top of the invisible spacer thead's identical reserved
  // height inside that scroll container, doubling the gap before the first data row.
  // Pulling the frozen box back up by its own measured height cancels that out, letting
  // it visually overlap the (invisible) spacer instead of stacking below it.
  const [frozenHeaderHeight, frozenHeaderWrapperRef] = useMeasuredElement<HTMLDivElement>(
    (el) => el.getBoundingClientRect().height,
  );
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const { allColsHaveWidths, totalTableWidth } = computeTableWidthInfo(columns, colWidths);

  const updateScrollIndicators = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScrollIndicators();
    el.addEventListener('scroll', updateScrollIndicators);
    window.addEventListener('resize', updateScrollIndicators);
    return () => {
      el.removeEventListener('scroll', updateScrollIndicators);
      window.removeEventListener('resize', updateScrollIndicators);
    };
  }, [updateScrollIndicators]);

  React.useEffect(() => {
    if (!onScrollContainerElement) return;
    onScrollContainerElement(scrollRef.current);
    return () => onScrollContainerElement(null);
  }, [onScrollContainerElement]);

  // ─── Column reorder ──────────────────────────────────────────

  function handleDrop(targetId: string) {
    if (!draggedColumnId || draggedColumnId === targetId) return;
    onColumnReorder?.(draggedColumnId, targetId);
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  }

  const resolveWidth = (colId: string, colWidth?: number) =>
    resolveColWidth(colWidths, colId, colWidth);
  const { hasRowLink, effectiveOnRowClick } = resolveRowLinkProps(renderRowLink, onRowClick);
  const buildRowProps = (item: T, idx: number) =>
    buildRowPropsFor(item, idx, {
      onRowClick: effectiveOnRowClick,
      getRowClassName,
      draggableRows,
      dragOverRowIndex,
      hasRowLink,
    });
  const renderRowLinkCell = renderRowLink
    ? (item: T) => <RowLinkCell>{renderRowLink(item)}</RowLinkCell>
    : undefined;

  const { headerCheckState, handleHeaderToggle, renderSelectionCell } = useRowSelectionState(
    rowSelection,
    data,
    getRowKey,
  );

  // Bundled once and passed to `renderHeaderRowCells` (data-table-header-row.tsx) for
  // both the real (possibly invisible-when-frozen) thead and the frozen header's own
  // copy — see that file's doc for why the row is rendered twice instead of moved
  // wholesale.
  const headerRowContext: HeaderRowContext<T> = {
    columns,
    hasRowLink,
    hasRowSelection: Boolean(rowSelection),
    headerCheckState,
    handleHeaderToggle,
    isResizing,
    draggedColumnId,
    dragOverColumnId,
    resizableColumns,
    reorderEnabled: Boolean(onColumnReorder),
    onColumnDragStart: setDraggedColumnId,
    onColumnDragOver: (colId, e) => {
      e.preventDefault();
      setDragOverColumnId(colId);
    },
    onColumnDrop: handleDrop,
    onColumnDragEnd: () => {
      setDraggedColumnId(null);
      setDragOverColumnId(null);
    },
    onColumnResizeStart: (e, colId) =>
      handleResizeStart(e, colId, snapshotThWidths(theadRef, columns)),
    onSort,
    sortBy,
    sortDir,
    sortOrders,
  };

  const frozenWidthResolver = (colId: string, colWidth?: number) =>
    measuredHeaderWidths[colId] ?? resolveWidth(colId, colWidth);

  return (
    <div className={cn('relative', virtualizedLayout.wrapperClassName, className)}>
      <ScrollIndicators canScrollLeft={canScrollLeft} canScrollRight={canScrollRight} />

      {useFrozenHeader ? (
        // Own sticky box, structurally independent of (a sibling of, not a descendant
        // of) the `overflow-x-auto` scroll container below — see `resolveUseFrozenHeader`
        // in data-table-hooks.ts for why. `overflow-hidden` clips the inner table to
        // this box's own width; `useFrozenHeaderSync`'s scroll listener shifts it left
        // in lockstep with the real scroll container's `scrollLeft`. No bottom
        // border/rounding here — the `bg-muted` header row (from TableHeader) against
        // the scroll container's own `bg-background` already reads as a clean edge
        // without stacking two borders.
        <div
          ref={frozenHeaderWrapperRef}
          className={frozenHeaderLayout.frozenWrapperClassName}
          style={{ ...stickyHeaderOffset.style, marginBottom: -frozenHeaderHeight }}
          data-testid="data-table-frozen-header"
        >
          <Table ref={frozenHeaderTableRef} className={DATA_TABLE_TEXT_CLASSNAME}>
            <TableHeader>
              <TableRow>
                {renderHeaderRowCells(headerRowContext, frozenWidthResolver, false)}
              </TableRow>
            </TableHeader>
          </Table>
        </div>
      ) : null}

      <div
        ref={scrollRef}
        className={cn(
          virtualizedLayout.scrollContainerClassName,
          frozenHeaderLayout.scrollContainerExtraClassName,
          scrollContainerClassName,
        )}
      >
        <Table
          className={DATA_TABLE_TEXT_CLASSNAME}
          data-testid="inventory-table"
          style={
            allColsHaveWidths
              ? { tableLayout: 'fixed', width: totalTableWidth }
              : { minWidth: '100%' }
          }
        >
          <TableHeader ref={theadRef} className={frozenHeaderLayout.realHeaderClassName}>
            <TableRow>
              {renderHeaderRowCells(headerRowContext, resolveWidth, useFrozenHeader)}
            </TableRow>
          </TableHeader>
          <TableBody style={virtualizedLayout.bodyStyle}>
            <TableRows
              virtualizer={virtualizer}
              columns={columns}
              data={data}
              getRowKey={getRowKey}
              getRowTestId={getRowTestId}
              getRowAttributes={getRowAttributes}
              resolveWidth={resolveWidth}
              buildRowProps={buildRowProps}
              renderRowLinkCell={renderRowLinkCell}
              renderSelectionCell={renderSelectionCell}
              draggableRows={draggableRows}
              onRowDragStart={handleRowDragStart}
              onRowDragOver={handleRowDragOver}
              onRowDrop={handleRowDrop}
              onRowDragEnd={handleRowDragEnd}
            />
          </TableBody>
        </Table>

        {/* Infinite-scroll sentinel — inside the scroll container so IntersectionObserver
            (owned by useTableVirtualizer) can measure it against the scrollable viewport. */}
        <TableVirtualizerSentinel sentinelRef={virtualizer?.sentinelRef} />

        {/* Footer */}
        {footer ? (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border/40 bg-muted/20 text-[12px] text-muted-foreground">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export { DataTable, resolveColResizable, ROW_LINK_ANCHOR_CLASSNAME, INTERACTIVE_CELL_CLASSNAME };
export type { DataTableProps, ColumnDef };
